'use client';
import React, { useState } from 'react';
import { Search, MapPin, Car, ArrowRight, Shield, Wrench, ChevronRight } from 'lucide-react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';

interface AutoPart {
  id: string;
  title: string;
  price: number;
  location: string;
  condition: string;
  category: string;
  seller: string;
  verified: boolean;
  compatibility: string;
  posted: string;
}

const PARTS: AutoPart[] = [
  { id: '1', title: 'Toyota Harrier 2.4 Engine (2AZ-FE)', price: 85000, location: 'Nairobi', condition: 'Used', category: 'Engines', seller: 'AutoParts Kenya', verified: true, compatibility: 'Toyota Harrier 2003–2013', posted: '2 hrs ago' },
  { id: '2', title: 'Nissan Note Front Bumper — White', price: 8500, location: 'Mombasa', condition: 'Used', category: 'Body Parts', seller: 'Coast Spares', verified: true, compatibility: 'Nissan Note E11 2005–2012', posted: '5 hrs ago' },
  { id: '3', title: 'Toyota Vitz Gearbox Automatic', price: 45000, location: 'Nairobi', condition: 'Used', category: 'Gearboxes', seller: 'Spares Hub', verified: false, compatibility: 'Toyota Vitz 1999–2005', posted: '1 day ago' },
  { id: '4', title: 'Subaru Forester Shock Absorbers (Set)', price: 12000, location: 'Nakuru', condition: 'New', category: 'Suspension', seller: 'Nakuru Auto', verified: true, compatibility: 'Subaru Forester SG 2002–2008', posted: '3 hrs ago' },
  { id: '5', title: 'Toyota Land Cruiser V8 Radiator', price: 22000, location: 'Nairobi', condition: 'Used', category: 'Cooling', seller: 'LC Spares', verified: true, compatibility: 'Toyota LC200 2007–2021', posted: '6 hrs ago' },
  { id: '6', title: 'Isuzu D-Max Brake Pads Front & Rear', price: 4500, location: 'Kisumu', condition: 'New', category: 'Brakes', seller: 'Western Spares', verified: false, compatibility: 'Isuzu D-Max 2012–2020', posted: '2 days ago' },
  { id: '7', title: 'Honda CRV Alternator 2.4L', price: 18000, location: 'Nairobi', condition: 'Used', category: 'Electrical', seller: 'AutoParts Kenya', verified: true, compatibility: 'Honda CRV RD4/RD5 2002–2006', posted: '4 hrs ago' },
  { id: '8', title: 'Mitsubishi Pajero Fuel Pump', price: 9500, location: 'Nairobi', condition: 'New', category: 'Fuel System', seller: 'Spares Hub', verified: true, compatibility: 'Mitsubishi Pajero V6/V7 2000–2006', posted: '1 day ago' },
];

const CATEGORIES = ['All', 'Engines', 'Gearboxes', 'Body Parts', 'Suspension', 'Brakes', 'Electrical', 'Cooling', 'Fuel System'];
const CONDITIONS = ['All', 'New', 'Used'];
const LOCATIONS = ['All Locations', 'Nairobi', 'Mombasa', 'Kisumu', 'Nakuru'];

export default function AutoPartsPage() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [condition, setCondition] = useState('All');
  const [location, setLocation] = useState('All Locations');

  const filtered = PARTS.filter(p => {
    const matchSearch = !search || p.title.toLowerCase().includes(search.toLowerCase()) || p.compatibility.toLowerCase().includes(search.toLowerCase());
    const matchCat = category === 'All' || p.category === category;
    const matchCond = condition === 'All' || p.condition === condition;
    const matchLoc = location === 'All Locations' || p.location === location;
    return matchSearch && matchCat && matchCond && matchLoc;
  });

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: '#070C1A' }}>
        {/* Hero */}
        <div className="relative px-4 pt-8 pb-10 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #1a0a00 0%, #0d1520 50%, #070C1A 100%)' }}>
          <div className="absolute inset-0 opacity-20"
            style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #FF6D00 0%, transparent 50%), radial-gradient(circle at 70% 30%, #FFB300 0%, transparent 50%)' }} />
          <div className="relative max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-600 mb-4"
              style={{ background: 'rgba(255,109,0,0.15)', color: '#FF6D00', border: '1px solid rgba(255,109,0,0.3)' }}>
              <Wrench size={12} /> 50,000+ Auto Parts Listed
            </div>
            <h1 className="text-3xl font-800 text-white mb-3 leading-tight">
              Find <span style={{ color: '#FF6D00' }}>Auto Parts</span> for Any Vehicle
            </h1>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Engines, gearboxes, body parts, and more. Sourced locally and from Japan, UK, and China.
            </p>
            <div className="relative max-w-md mx-auto">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by part name or vehicle model…"
                className="w-full pl-10 pr-4 py-3.5 rounded-2xl text-sm outline-none"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }} />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="px-4 py-3 sticky top-0 z-20"
          style={{ background: 'rgba(7,12,26,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pb-1">
            {CATEGORIES.map(c => (
              <button key={c} onClick={() => setCategory(c)}
                className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
                style={{ background: category === c ? 'rgba(255,109,0,0.15)' : 'rgba(255,255,255,0.04)', color: category === c ? '#FF6D00' : 'rgba(255,255,255,0.55)', border: `1px solid ${category === c ? 'rgba(255,109,0,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {c}
              </button>
            ))}
          </div>
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pt-2">
            {CONDITIONS.map(c => (
              <button key={c} onClick={() => setCondition(c)}
                className="px-3 py-1.5 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
                style={{ background: condition === c ? 'rgba(0,200,83,0.15)' : 'rgba(255,255,255,0.04)', color: condition === c ? '#00C853' : 'rgba(255,255,255,0.45)', border: `1px solid ${condition === c ? 'rgba(0,200,83,0.3)' : 'rgba(255,255,255,0.07)'}` }}>
                {c}
              </button>
            ))}
            {LOCATIONS.map(l => (
              <button key={l} onClick={() => setLocation(l)}
                className="px-3 py-1.5 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
                style={{ background: location === l ? 'rgba(255,109,0,0.15)' : 'rgba(255,255,255,0.04)', color: location === l ? '#FF6D00' : 'rgba(255,255,255,0.45)', border: `1px solid ${location === l ? 'rgba(255,109,0,0.3)' : 'rgba(255,255,255,0.07)'}` }}>
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 pt-4 pb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <span style={{ color: '#FF6D00', fontWeight: 700 }}>{filtered.length}</span> parts found
            </span>
            <Link href="/login"
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-700"
              style={{ background: '#FF6D00', color: '#fff' }}>
              Sell Parts <ArrowRight size={12} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {filtered.map(part => (
              <div key={part.id} className="p-4 rounded-2xl"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(255,109,0,0.12)', border: '1px solid rgba(255,109,0,0.2)' }}>
                    <Car size={18} style={{ color: '#FF6D00' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-sm font-700 text-white leading-tight">{part.title}</h3>
                      <span className="text-sm font-800 flex-shrink-0" style={{ color: '#FF6D00' }}>
                        KES {part.price.toLocaleString()}
                      </span>
                    </div>
                    <p className="text-[10px] mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Fits: {part.compatibility}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className="text-[10px] px-1.5 py-0.5 rounded-md font-600"
                        style={{ background: part.condition === 'New' ? 'rgba(0,200,83,0.15)' : 'rgba(255,179,0,0.15)', color: part.condition === 'New' ? '#00C853' : '#FFB300' }}>
                        {part.condition}
                      </span>
                      <div className="flex items-center gap-1">
                        <MapPin size={10} style={{ color: 'rgba(255,255,255,0.35)' }} />
                        <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{part.location}</span>
                      </div>
                      {part.verified && <Shield size={10} style={{ color: '#00C853' }} />}
                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>{part.posted}</span>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>{part.seller}</span>
                      <Link href="/login"
                        className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-700"
                        style={{ background: 'rgba(255,109,0,0.15)', color: '#FF6D00', border: '1px solid rgba(255,109,0,0.25)' }}>
                        Contact <ChevronRight size={11} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 rounded-3xl text-center"
            style={{ background: 'linear-gradient(135deg, rgba(255,109,0,0.1), rgba(255,179,0,0.1))', border: '1px solid rgba(255,109,0,0.2)' }}>
            <h3 className="text-lg font-800 text-white mb-2">List Your Auto Parts</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>Reach thousands of buyers looking for spare parts across Kenya.</p>
            <Link href="/login"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
              style={{ background: '#FF6D00', color: '#fff' }}>
              <Wrench size={16} /> Start Selling Parts
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
